import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Sparkles, Target } from 'lucide-react';
import { cn } from '../../lib/utils';
import { gamesAdminApi, type GameLimits } from '../../api/games';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ErrorState, StatusPill } from '@/components/app';
import { Card, StepperField, TabIntro } from './controls';
import { getErrorMessage } from '@/lib/errors';
import { formatTashkentShort, formatTashkentTime } from '@/lib/datetime';

type LimitKey = keyof GameLimits;

/** Допустимые диапазоны полей (целые числа). Бэкенд проверяет только «целое ≥ 0». */
const LIMIT_RANGES: Record<LimitKey, [number, number]> = {
  duel_free_daily: [0, 100],
  time_attack_free_daily: [0, 100],
  survival_free_daily: [0, 100],
  daily_freeze_per_week: [0, 7],
  daily_replay_window_days: [1, 365],
  ai_explain_free_daily: [0, 100],
};

/** Значения по умолчанию — default колонок backend/src/modules/games/entities/game-limits.entity.ts. */
const LIMIT_DEFAULTS: Record<LimitKey, number> = {
  duel_free_daily: 1,
  time_attack_free_daily: 3,
  survival_free_daily: 3,
  daily_freeze_per_week: 2,
  daily_replay_window_days: 30,
  ai_explain_free_daily: 3,
};

const FIELDS: Record<LimitKey, { label: string; short: string; help: string }> = {
  duel_free_daily: {
    label: 'Дуэль — бесплатно в день',
    short: 'дуэль',
    help: 'Попытка списывается при поиске соперника. Ноль закрывает игру для Free.',
  },
  time_attack_free_daily: {
    label: 'Spidran — бесплатно в день',
    short: 'Spidran',
    help: 'Тайм-атака: ответить на максимум вопросов за отведённое время.',
  },
  survival_free_daily: {
    label: 'Hayot — бесплатно в день',
    short: 'Hayot',
    help: 'Выживание: игра до первой ошибки. Второй шанс только у Premium.',
  },
  daily_freeze_per_week: {
    label: 'Заморозок серии в неделю',
    short: 'заморозки серии',
    help: 'Сколько пропущенных дней в неделю не сбрасывают серию.',
  },
  daily_replay_window_days: {
    label: 'Окно доигрывания, дней',
    short: 'окно доигрывания',
    help: 'Насколько глубоко Premium может доиграть пропущенные дни.',
  },
  ai_explain_free_daily: {
    label: 'Бесплатных AI-разборов в день',
    short: 'AI-разборы',
    help: 'Сколько раз в сутки Free может открыть AI-разбор вопроса. Сбрасывается в полночь по Ташкенту, не по UTC. Ноль — разбор только для Premium.',
  },
};

const GAME_KEYS: LimitKey[] = ['duel_free_daily', 'time_attack_free_daily', 'survival_free_daily'];
const DAILY_KEYS: LimitKey[] = ['daily_freeze_per_week', 'daily_replay_window_days'];
const ALL_KEYS = Object.keys(LIMIT_RANGES) as LimitKey[];

function rangeError(field: LimitKey, value: number | undefined): string | null {
  const [min, max] = LIMIT_RANGES[field];
  if (value === undefined || !Number.isInteger(value) || value < min || value > max) {
    return `Целое число от ${min} до ${max}`;
  }
  return null;
}

/** 1 поле, 2 поля, 5 полей. */
function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/**
 * Вкладка «Лимиты» — дневные нормы бесплатного доступа к играм.
 * Раньше это были константы в коде, любая правка баланса требовала деплоя.
 */
export default function LimitsTab() {
  const [limits, setLimits] = useState<GameLimits | null>(null);
  const [draft, setDraft] = useState<GameLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await gamesAdminApi.gameLimits();
      setLimits(data);
      setDraft(data);
    } catch (err: unknown) {
      setLoadError(getErrorMessage(err, 'Не удалось загрузить лимиты'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fieldErrors = draft
    ? ALL_KEYS.reduce<Partial<Record<LimitKey, string>>>((acc, field) => {
        const err = rangeError(field, draft[field]);
        if (err) acc[field] = err;
        return acc;
      }, {})
    : {};
  const errorCount = Object.keys(fieldErrors).length;
  const hasErrors = errorCount > 0;

  const changedKeys = draft && limits ? ALL_KEYS.filter((k) => draft[k] !== limits[k]) : [];
  const dirty = changedKeys.length > 0;

  const save = async () => {
    if (!draft || hasErrors || !dirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Шлём только редактируемые поля: в draft лежит и то, что пришло из GET
      // (id, updated_at), а серверу это не нужно.
      const next = await gamesAdminApi.updateGameLimits({
        duel_free_daily: draft.duel_free_daily,
        time_attack_free_daily: draft.time_attack_free_daily,
        survival_free_daily: draft.survival_free_daily,
        daily_freeze_per_week: draft.daily_freeze_per_week,
        daily_replay_window_days: draft.daily_replay_window_days,
        ai_explain_free_daily: draft.ai_explain_free_daily,
      });
      setLimits(next);
      setDraft(next);
      setSavedAt(new Date());
    } catch (err: unknown) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить лимиты'));
    } finally {
      setSaving(false);
    }
  };

  const set = (patch: Partial<GameLimits>) => setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const field = (key: LimitKey, layout?: 'card' | 'inline') => {
    const [min, max] = LIMIT_RANGES[key];
    const meta = FIELDS[key];
    return (
      <StepperField
        key={key}
        id={`limit-${key}`}
        label={meta.label}
        shortLabel={meta.short}
        value={draft![key] ?? 0}
        min={min}
        max={max}
        error={fieldErrors[key]}
        changedFrom={limits && draft![key] !== limits[key] ? limits[key] : null}
        onChange={(v) => set({ [key]: v } as Partial<GameLimits>)}
        layout={layout}
        help={
          <>
            {meta.help} <span className="tabular-nums">По умолчанию: {LIMIT_DEFAULTS[key]}.</span>
          </>
        }
      />
    );
  };

  const updatedAt = (limits as (GameLimits & { updated_at?: string }) | null)?.updated_at;

  if (loadError && !draft) {
    return (
      <div>
        <ErrorState message={loadError} onRetry={load} className="rounded-card border border-border-soft bg-surface" />
      </div>
    );
  }

  return (
    <form
      aria-label="Лимиты бесплатного доступа"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      onReset={(e) => {
        e.preventDefault();
        setDraft(limits);
        setSaveError(null);
      }}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-text-2">
          Правки доезжают до сервера за полминуты, релиз не нужен
          {savedAt ? (
            <> · сохранено в <span className="tabular-nums">{formatTashkentTime(savedAt)}</span></>
          ) : updatedAt ? (
            <> · сохранено <span className="tabular-nums">{formatTashkentShort(updatedAt)}</span></>
          ) : null}
        </span>
        <Button type="button" variant="secondary" onClick={load} disabled={loading}>
          <RefreshCw className={cn(loading && 'animate-spin')} aria-hidden />
          Обновить
        </Button>
      </div>

      <TabIntro>
        Лимиты действуют только на Free: у Premium попыток не считаем. Игровой счётчик списывается при старте игры и
        обнуляется в 00:00 UTC — это 05:00 по Ташкенту.
      </TabIntro>

      {!draft ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[150px] rounded-card" />
          ))}
        </div>
      ) : (
        <>
          <section aria-labelledby="sec-games" className="flex flex-col gap-2.5">
            <h2 id="sec-games" className="m-0 font-display text-[17px] font-bold">Бесплатных игр в день</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">{GAME_KEYS.map((k) => field(k))}</div>
          </section>

          <section aria-labelledby="sec-daily" className="flex flex-col gap-2.5">
            <h2 id="sec-daily" className="m-0 font-display text-[17px] font-bold">Вызов дня · только Premium</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{DAILY_KEYS.map((k) => field(k))}</div>
          </section>

          <section
            aria-label="AI-разборы вопросов"
            className="flex items-start gap-3.5 rounded-card bg-accent-soft px-5 py-[18px] shadow-[inset_0_0_0_2px_var(--accent)]"
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-accent text-on-accent" aria-hidden>
              <Sparkles className="size-5" />
            </span>
            <div className="min-w-0 flex-1">{field('ai_explain_free_daily', 'inline')}</div>
          </section>

          <Card title="Что остаётся только у премиума" icon={<Target />}>
            <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-sm text-text-2">
              <li>Дуэль по выбранной теме</li>
              <li>Второй шанс после ошибки в «Выживании»</li>
              <li>Доигрывание пропущенных дней и заморозка серии</li>
              <li>Безлимитные попытки во всех играх</li>
            </ul>
            <p className="m-0 text-xs text-text-2">Это граница подписки, а не настройка — меняется только в коде.</p>
          </Card>

          <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-surface px-[18px] py-3 shadow-pop">
            <span role="status" className="flex flex-wrap items-center gap-3">
              {dirty ? (
                <>
                  <b className="tabular-nums">
                    Изменено: {changedKeys.length} {plural(changedKeys.length, 'поле', 'поля', 'полей')}
                  </b>
                  <span className="text-[13px] text-text-2">
                    {changedKeys
                      .map((k) => `${FIELDS[k].short.charAt(0).toUpperCase()}${FIELDS[k].short.slice(1)} ${limits![k]} → ${Number.isFinite(draft[k]) ? draft[k] : '—'}`)
                      .join(' · ')}
                  </span>
                </>
              ) : (
                <span className="text-[13px] text-text-2">
                  {savedAt ? `Сохранено в ${formatTashkentTime(savedAt)}` : 'Изменений нет'}
                </span>
              )}
              {hasErrors && (
                <StatusPill tone="neg">
                  {errorCount} {plural(errorCount, 'ошибка', 'ошибки', 'ошибок')} — исправьте перед сохранением
                </StatusPill>
              )}
              {saveError && <StatusPill tone="neg">{saveError}</StatusPill>}
            </span>
            <span className="flex gap-2">
              <Button type="reset" variant="secondary" disabled={!dirty || saving}>
                Отменить
              </Button>
              <Button type="submit" disabled={!dirty || hasErrors || saving}>
                {saving ? 'Сохраняю…' : 'Сохранить изменения'}
              </Button>
            </span>
          </div>
        </>
      )}
    </form>
  );
}
